import DragIdeas from "./plate_viewer/drag_ideas"
import PointerDownHandler from "./plate_viewer/pointer_down_handler"
import * as _ from "lodash"
import PixiRenderer from "./plate_viewer/pixi_renderer"
import {
  creationTimeIdeaColourResolver,
  defaultIdeaColourResolver,
  editTimeIdeaColourResolver,
  lastViewedTimeIdeaColourResolver,
  selfIdeaColourResolver,
  type IdeaColourMode,
} from "./plate_viewer/idea_colour"
import {
  type CameraState,
  type PlaneLocation,
  grid_to_plane,
  grid_to_view,
  plane_to_grid,
  plane_to_view,
  view_to_grid,
  view_to_plane,
} from "./plate_viewer/coords"
import { BOX_WIDTH } from "./plate_viewer/grid"
import {
  Idea,
  ID,
  LocationDiff,
  Location,
  FleetingIdea,
  ideaWidth,
  ideaHeight,
} from "./model/types"

import hotkeys from "hotkeys-js"
import { TitleHoverState } from "./app/routes/home/mosaic_page/show_plate_view/title_hover"
import { ContextMenuState } from "./app/routes/home/mosaic_page/show_plate_view/context_menu"
import Model from "./model/model"
import Hub from "./hub"
import TouchHandler from "./plate_viewer/touch_handler"
import { type DeviceContextProps } from "./app/providers/device_context"
import { NavigationHistoryState } from "./app/routes/home/mosaic"
import Commander from "./model/commander"
import { placeFleetingIdeaCommands } from "./model/commands/fleeting_idea/fleeting_idea_helper"

const DEFAULT_APP_WIDTH = 600
const DEFAULT_APP_HEIGHT = 600
const FAST_ARROW_JUMP_SIZE = 5
const MAX_MOSAIC_SCALE = 6
const MIN_MOSAIC_SCALE = 0.3
const FULL_TITLE_ENTER_SCALE = 2.4
const FULL_TITLE_EXIT_SCALE = 2.0
const FULL_TITLE_VIEWPORT_MARGIN_CELLS = 2

type LassoBox = {
  gx: number
  gy: number
  width: number
  height: number
}

type PlaneOffset = {
  dpx: number
  dpy: number
}

type ViewBounds = {
  x: number
  y: number
  scale: number
}

class PlateView {
  model: Model
  commander: Commander
  hub: Hub
  domElement: HTMLElement | null
  canvas: HTMLCanvasElement
  appWidth: number
  appHeight: number
  scale: number
  initialIdeas: Idea[]

  openedCell: null | Location

  initialOpenCell: null | Location
  initialViewBounds: null | ViewBounds

  selectedIdeaIds: ID[]
  lassoBox: null | LassoBox

  darkMode: boolean
  darkModeMediaQuery: MediaQueryList | null
  darkModeMediaQueryListener: ((event: MediaQueryListEvent) => void) | null
  colourMode: IdeaColourMode
  extents: {
    min: { gx: number; gy: number }
    max: { gx: number; gy: number }
  }

  renderer: PixiRenderer

  dragIdeas: null | DragIdeas
  viewBoundRightWhenOpened: number
  fullTitleModeActive: boolean
  fullTitleRefreshFrame: number | null

  setContextMenuState: (x: ContextMenuState | null) => void
  setTitleHoverAt: (x: TitleHoverState | null) => void
  draggingFleetingIdea: null | FleetingIdea
  setPlateViewDraggingHappening: (b: boolean) => void
  viewBoundsChanged: (vb: ViewBounds) => void

  constructor(
    setContextMenuState: (x: ContextMenuState | null) => void,
    setTitleHoverAt: (x: TitleHoverState | null) => void,
    setPlateViewDraggingHappening: (b: boolean) => void,
    initialOpenCell: null | Location,
    viewBoundsChanged: (vb: ViewBounds) => void,
    initialViewBounds: ViewBounds | null,
    private deviceContext: DeviceContextProps,
    public lockedRef: React.MutableRefObject<boolean>,
    private clickHereFirstRef: React.MutableRefObject<HTMLDivElement | null>,
  ) {
    this.setContextMenuState = setContextMenuState
    this.setTitleHoverAt = setTitleHoverAt
    this.setPlateViewDraggingHappening = setPlateViewDraggingHappening
    this.initialOpenCell = initialOpenCell
    this.viewBoundsChanged = viewBoundsChanged
    this.initialViewBounds = initialViewBounds
    this.dragIdeas = null
    this.darkModeMediaQuery = null
    this.darkModeMediaQueryListener = null
    this.colourMode = "default"
    this.fullTitleModeActive = false
    this.fullTitleRefreshFrame = null
  }

  setup(model: Model, commander: Commander, hub: Hub) {
    if (!this.domElement) throw new Error("domElement not set in PlateView.setup")

    this.model = model
    this.commander = commander
    this.hub = hub
    const ideas = Array.from(model.store.ideas.values())
    this.initialIdeas = ideas

    this.scale = 1
    this.selectedIdeaIds = []
    this.lassoBox = null
    this.setupDarkModeListener()

    this.viewBoundRightWhenOpened = 800

    this.extents = ideas.reduce(
      (result, idea) => {
        if (idea.location.gx < result.min.gx) result.min.gx = idea.location.gx
        if (idea.location.gy < result.min.gy) result.min.gy = idea.location.gy
        if (idea.location.gx > result.max.gx) result.max.gx = idea.location.gx
        if (idea.location.gy > result.max.gy) result.max.gy = idea.location.gy
        return result
      },
      {
        min: { gx: 0, gy: 0 },
        max: { gx: -0, gy: -0 },
      },
    )

    this.render(this.domElement)

    this.renderFreshIdeas()
    this.renderer.render_onscreen_idea_texts(this.initialIdeas, (idea) => this.isIdeaOnscreen(idea))
    this.renderer.lazy_render_idea_texts(this.initialIdeas)
    this.updateFullTitleMode({ immediate: true })

    this.canvas = this.renderer.canvas()

    this.domElement.appendChild(this.canvas)
    this.canvas.addEventListener("contextmenu", (e) => e.preventDefault())

    this.hub.on("navigation-history:open-location-set", ({ location }) => {
      if (location === null) this.clearOpenedSquare()
      else this.setOpenedSquare(location)
    })

    this.hub.on("model:idea-created", ({ id }) => {
      this.ideaAdded(id)
    })

    this.hub.on("command:delete-ideas", ({ captured }) => {
      if (!captured) throw new Error("Undo state not set in command:delete-ideas")
      this.removeIdeas(captured.ideas)
    })

    this.hub.on("command:move-ideas", ({ ideaLocations }) => this.moveIdeas(ideaLocations))

    this.hub.on("command:colour-ideas", ({ idColours }) => {
      idColours.forEach(([id, _]) => {
        const idea = this.model.fetchIdea(id)
        this.renderer.rerender_idea_text(idea)
      })
      this.renderFreshIdeas()
    })

    this.hub.on("command:view-idea", ({ id }) => this.ideaUpdated(id, "lastViewedAt"))
    this.hub.on("command:set-idea-field", ({ id, field }) => this.ideaUpdated(id, field))
    this.hub.on("command:set-idea-source", ({ id }) => this.ideaUpdated(id, "sourceType"))
    this.hub.on("model:idea-connections-changed", () => this.renderFreshIdeas())
  }

  setColourMode(colourMode: IdeaColourMode): void {
    if (this.colourMode === colourMode) return

    this.colourMode = colourMode
    this.renderFreshIdeas()
    this.renderer.rerender_rendered_idea_texts(this.model)
    this.scheduleFullTitleRefresh({ immediate: true })
  }

  private updateIdeaColourResolver(): void {
    if (!this.renderer) return

    const resolver =
      this.colourMode === "creationTime"
        ? creationTimeIdeaColourResolver(this.model.store.ideas.values())
        : this.colourMode === "editTime"
          ? editTimeIdeaColourResolver(this.model.store.ideas.values())
          : this.colourMode === "lastViewedTime"
            ? lastViewedTimeIdeaColourResolver(this.model.store.ideas.values())
            : this.colourMode === "self"
              ? selfIdeaColourResolver
              : defaultIdeaColourResolver

    this.renderer.set_idea_colour_resolver(resolver)
  }

  private renderFreshIdeas(hidingIdeas: Idea[] = []): void {
    this.updateIdeaColourResolver()
    this.renderer.render_fresh_ideas(this.model, hidingIdeas)
  }

  private setupDarkModeListener(): void {
    this.removeDarkModeListener()

    if (!window.matchMedia) {
      this.darkMode = false
      return
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.darkMode = mediaQuery.matches
    this.darkModeMediaQuery = mediaQuery
    this.darkModeMediaQueryListener = (event) => this.setDarkMode(event.matches)

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", this.darkModeMediaQueryListener)
    } else {
      mediaQuery.addListener(this.darkModeMediaQueryListener)
    }
  }

  private removeDarkModeListener(): void {
    if (!this.darkModeMediaQuery || !this.darkModeMediaQueryListener) return

    if (this.darkModeMediaQuery.removeEventListener) {
      this.darkModeMediaQuery.removeEventListener("change", this.darkModeMediaQueryListener)
    } else {
      this.darkModeMediaQuery.removeListener(this.darkModeMediaQueryListener)
    }

    this.darkModeMediaQuery = null
    this.darkModeMediaQueryListener = null
  }

  private setDarkMode(isDarkMode: boolean): void {
    if (this.darkMode === isDarkMode) return

    this.darkMode = isDarkMode
    if (!this.renderer) return

    this.renderer.set_dark_mode(isDarkMode)
    if (this.openedCell) this.renderOpenedCellOverlay()
  }

  private renderOpenedCellOverlay(): void {
    if (!this.openedCell) return

    const idea = this.model.getIdeaAt(this.openedCell)
    this.renderer.render_open_cell_overlay({
      location: this.openedCell,
      is_dark_mode: this.darkMode,
      width: idea ? ideaWidth(idea) : 1,
      height: idea ? ideaHeight(idea) : 1,
    })
  }

  setDomElement(domElement: HTMLElement) {
    this.domElement = domElement
    this.appWidth = domElement.clientWidth || DEFAULT_APP_WIDTH
    this.appHeight = domElement.clientHeight || DEFAULT_APP_HEIGHT

    if (this.canvas) this.domElement.appendChild(this.canvas)

    if (this.renderer) this.renderer.set_resize_target(this.domElement)

    this.domElement.addEventListener("dragover", (e) => {
      e.preventDefault()
    })
    this.domElement.addEventListener("drop", (e) => {
      const { gx, gy } = view_to_grid(
        {
          vx: e.offsetX,
          vy: e.offsetY,
        },
        this.camera_state(),
      )

      if (this.draggingFleetingIdea) {
        const idea = this.draggingFleetingIdea
        if (!this.getIdeaByCoords(gx, gy)) {
          const [c1, c2] = placeFleetingIdeaCommands(this.model, idea, {
            gx,
            gy,
          })
          this.commander.exec(c1)
          this.commander.exec(c2)
        }
        this.draggingFleetingIdea = null
      }
    })
  }

  clearDomElement() {
    this.domElement = null
  }

  getIdea(idea_id: ID): Idea {
    return this.model.fetchIdea(idea_id)
  }

  getIdeaByCoords(gx, gy) {
    return this.model.getIdeaAt({ gx, gy })
  }

  selectedIdeas(): Idea[] {
    return this.selectedIdeaIds
      .map((id) => this.model.getIdea(id))
      .filter((idea: Idea | null): idea is Idea => idea !== null)
  }

  render(domElement: HTMLElement) {
    this.renderer = new PixiRenderer({
      dom_element: domElement,
      app_width: this.appWidth,
      app_height: this.appHeight,
      is_dark_mode: this.darkMode,
    })

    if (this.initialOpenCell) {
      this.setOpenedSquare(this.initialOpenCell)
    } else if (this.initialViewBounds) {
      this.renderer.apply_view_bounds(this.initialViewBounds)
      this.scale = this.initialViewBounds.scale
    }
    this.renderer.reposition_grid(this.camera_state())

    let prevPinchScale: number = 1
    let initialCenter: { px: number; py: number } | null = null

    const touchHandler = new TouchHandler(
      this.renderer.canvas(),
      (centre) => {
        prevPinchScale = 1
        initialCenter = view_to_plane(
          {
            vx: centre.vx,
            vy: centre.vy,
          },
          this.camera_state(),
        )
        dragView = false
        this.dragIdeas = null
      },
      (scaleFactor) => {
        const incrementalScaleFactor = scaleFactor / prevPinchScale
        if (initialCenter) this.scaleMosaic(incrementalScaleFactor, initialCenter)
        prevPinchScale = scaleFactor
        this.closeContextMenu()
      },
    )

    let pointerDownHandler: PointerDownHandler | null = null
    this.dragIdeas = null
    let dragView = false

    this.renderer.on_stage_event("pointerdown", (e) => {
      if (touchHandler.active()) return
      this.setPlateViewDraggingHappening(true)
      this.renderer.set_hover_visible(false)

      pointerDownHandler = new PointerDownHandler(e, this.renderer.stage_position(), this, () => {})

      pointerDownHandler.pointerDown(e, ({ lassoing, rightClick, start }) => {
        if (rightClick) return
        if (lassoing) return

        const targetedIdea = this.model.getIdeaAt(start)
        if (targetedIdea && this.lockedRef.current === false) {
          if (this.selectedIdeaIds.includes(targetedIdea.id)) {
            dragView = false
            this.renderer.set_stage_cursor("grabbing")
            this.dragIdeas = new DragIdeas(this.selectedIdeaIds, true, this)
          } else {
            dragView = false
            this.renderer.set_stage_cursor("grabbing")
            this.dragIdeas = new DragIdeas([targetedIdea.id], false, this)
          }
        } else {
          dragView = true
        }
      })
    })

    this.renderer.on_stage_event("pointermove", (e) => {
      if (touchHandler.active()) return
      if (!this.domElement) return

      const { x: vx, y: vy } = e.global
      const outsideWindow =
        vx < 0 || vy < 0 || vx > this.domElement.clientWidth || vy > this.domElement.clientHeight
      if (pointerDownHandler) {
        if (outsideWindow) {
          pointerDownHandler = null
          this.dragIdeas?.abort()
          this.dragIdeas = null
          dragView = false
          this.renderer.set_stage_cursor("default")
        } else {
          this.closeContextMenu()

          pointerDownHandler.pointerMove(
            e,
            ({
              lassoing,
              current: { gx, gy, dvx, dvy, dpx, dpy },
              start: { gx: zgx, gy: zgy, sx, sy },
            }) => {
              this.hideTitleHover()
              this.renderer.set_hover_visible(false)

              if (dragView) {
                this.dragIdeas?.abort()
                this.dragIdeas = null
                this.movePlane({
                  vx: dvx + sx,
                  vy: dvy + sy,
                })
              } else if (lassoing) {
                this.dragIdeas?.abort()
                this.dragIdeas = null
                dragView = false

                this.lassoArea({
                  gx1: Math.min(zgx, gx),
                  gy1: Math.min(zgy, gy),
                  gx2: Math.max(zgx, gx),
                  gy2: Math.max(zgy, gy),
                })
              } else if (this.dragIdeas) {
                this.dragIdeas.showMovingWithCursor({ dpx, dpy })
              }
            },
          )
        }
      } else {
        this.hoverOver(
          view_to_grid(
            {
              vx: e.data.global.x,
              vy: e.data.global.y,
            },
            this.camera_state(),
          ),
          e,
        )

        if (outsideWindow) {
          this.clearLasso({ addToSelection: hotkeys.isPressed("shift") })
        }
      }
    })

    this.renderer.on_stage_event("pointerup", (e) => {
      if (touchHandler.active()) return
      this.setPlateViewDraggingHappening(false)

      if (!this.deviceContext.isTouchDevice) this.renderer.set_hover_visible(true)
      if (pointerDownHandler)
        pointerDownHandler.pointerUp(
          e,
          ({ isClick, rightClick, current: { gx, gy, dgx, dgy, px, py } }) => {
            if (isClick) {
              if (this.deviceContext.isTouchDevice) {
                NavigationHistoryState.set_open_location({ gx, gy })
                if (!this.model.getIdeaAt({ gx, gy })) {
                  this.openContextMenuAt({ px, py })
                }
              } else {
                NavigationHistoryState.set_open_location({ gx, gy })
              }
            } else if (rightClick) {
              this.openContextMenuAt({ px, py })
            } else {
              this.clearLasso({ addToSelection: hotkeys.isPressed("shift") })
              this.dragIdeas?.place({ dx: dgx, dy: dgy })

              this.viewBoundsChanged(this.renderer.view_bounds(this.scale))
            }
          },
        )
      pointerDownHandler = null
      this.dragIdeas?.abort()
      this.dragIdeas = null
      dragView = false
    })

    this.renderer.on_stage_event("pointerover", (e) => {
      if (!this.deviceContext.isTouchDevice) this.renderer.set_hover_visible(true)
    })

    this.renderer.on_stage_event("pointerout", (e) => {
      this.renderer.set_hover_visible(false)
    })

    this.renderer.on_stage_event("wheel", (e) => {
      const { deltaY } = e
      const { px, py } = view_to_plane(
        {
          vx: e.data.global.x,
          vy: e.data.global.y,
        },
        this.camera_state(),
      )
      let scaleFactor = 1 - deltaY / 300
      this.scaleMosaic(scaleFactor, { px, py })
      this.closeContextMenu()
    })

    // Necessary otherwise pinch to zoom on desktop trackpad zooms the whole page
    const canvas = this.renderer.canvas()
    canvas.addEventListener("wheel", (e) => {
      e.preventDefault()
    })
  }

  scaleMosaic(scaleFactor: number, { px, py }: { px: number; py: number }) {
    if (this.scale * scaleFactor > MAX_MOSAIC_SCALE) scaleFactor = MAX_MOSAIC_SCALE / this.scale
    if (this.scale * scaleFactor < MIN_MOSAIC_SCALE) scaleFactor = MIN_MOSAIC_SCALE / this.scale
    const newScale = this.scale * scaleFactor
    const dpx = px / scaleFactor - px
    const dpy = py / scaleFactor - py
    this.scale = newScale
    this.renderer.scale_view({ new_scale: newScale, dpx, dpy })
    this.viewBoundsChanged(this.renderer.view_bounds(this.scale))

    this.updateFullTitleMode()
    this.updateClickHereFirstLocation()
  }

  private updateFullTitleMode({ immediate }: { immediate?: boolean } = {}): void {
    if (!this.renderer) return

    const shouldBeActive = this.fullTitleModeActive
      ? this.scale > FULL_TITLE_EXIT_SCALE
      : this.scale >= FULL_TITLE_ENTER_SCALE

    if (shouldBeActive === this.fullTitleModeActive) {
      if (shouldBeActive) this.scheduleFullTitleRefresh({ immediate })
      return
    }

    this.fullTitleModeActive = shouldBeActive
    this.renderer.set_compact_idea_texts_visible(!shouldBeActive)

    if (shouldBeActive) {
      this.hideTitleHover()
      this.scheduleFullTitleRefresh({ immediate: true })
    } else {
      this.cancelScheduledFullTitleRefresh()
      this.renderer.clear_full_titles()
    }
  }

  private scheduleFullTitleRefresh({ immediate }: { immediate?: boolean } = {}): void {
    if (!this.fullTitleModeActive) return

    if (immediate) {
      this.cancelScheduledFullTitleRefresh()
      this.renderVisibleFullTitles()
      return
    }

    if (this.fullTitleRefreshFrame != null) return

    if (typeof window.requestAnimationFrame !== "function") {
      this.renderVisibleFullTitles()
      return
    }

    this.fullTitleRefreshFrame = window.requestAnimationFrame(() => {
      this.fullTitleRefreshFrame = null
      this.renderVisibleFullTitles()
    })
  }

  private cancelScheduledFullTitleRefresh(): void {
    if (this.fullTitleRefreshFrame == null) return

    if (typeof window.cancelAnimationFrame === "function")
      window.cancelAnimationFrame(this.fullTitleRefreshFrame)
    this.fullTitleRefreshFrame = null
  }

  private renderVisibleFullTitles(): void {
    if (!this.fullTitleModeActive) return
    this.renderer.render_full_titles(this.visibleIdeasForFullTitles())
  }

  private visibleIdeasForFullTitles(): Idea[] {
    const cameraState = this.camera_state()
    const topLeft = view_to_grid({ vx: 0, vy: 0 }, cameraState)
    const bottomRight = view_to_grid(
      {
        vx: this.renderer.screen_width(),
        vy: this.renderer.screen_height(),
      },
      cameraState,
    )
    const minGx = Math.min(topLeft.gx, bottomRight.gx) - FULL_TITLE_VIEWPORT_MARGIN_CELLS
    const maxGx = Math.max(topLeft.gx, bottomRight.gx) + FULL_TITLE_VIEWPORT_MARGIN_CELLS
    const minGy = Math.min(topLeft.gy, bottomRight.gy) - FULL_TITLE_VIEWPORT_MARGIN_CELLS
    const maxGy = Math.max(topLeft.gy, bottomRight.gy) + FULL_TITLE_VIEWPORT_MARGIN_CELLS
    const seenIds = new Set<ID>()
    const ideas: Idea[] = []

    for (let gx = minGx; gx <= maxGx; gx++) {
      for (let gy = minGy; gy <= maxGy; gy++) {
        const idea = this.model.getIdeaAt({ gx, gy })
        if (!idea || seenIds.has(idea.id) || (!idea.title && !idea.memo)) continue

        seenIds.add(idea.id)
        ideas.push(idea)
      }
    }

    return ideas
  }

  updateClickHereFirstLocation() {
    if (this.clickHereFirstRef.current) {
      // magic numbers are the location of the How square in the empty state account
      const { vx, vy } = grid_to_view(
        {
          gx: -1,
          gy: -8,
        },
        this.camera_state(),
      )
      this.clickHereFirstRef.current.style.top = `${vy}px`
      this.clickHereFirstRef.current.style.left = `${vx}px`
      this.clickHereFirstRef.current.style.transform = `scale(${this.scale})`
      this.clickHereFirstRef.current.style.transformOrigin = `top left`
    }
  }

  camera_state(): CameraState {
    return this.renderer.camera_state(this.scale)
  }

  hoverOver(loc: Location, event?) {
    const topElement = document.elementFromPoint(event.clientX, event.clientY)

    if (topElement !== this.canvas) {
      this.renderer.hide_pointer_square()
      this.hideTitleHover()
      return
    }

    let { px, py } = grid_to_plane(loc)
    const idea = this.model.getIdeaAt(loc)
    let width = 1,
      height = 1
    if (idea) {
      width = ideaWidth(idea)
      height = ideaHeight(idea)
      let { px: px1, py: py1 } = grid_to_plane({
        gx: idea.location.gx,
        gy: idea.location.gy,
      })
      px = px1
      py = py1
    }
    this.renderer.show_pointer_square({ px, py, width, height })

    if (!this.fullTitleModeActive && !this.dragIdeas && idea && idea.title) {
      const { vx, vy } = grid_to_view(idea.location, this.camera_state())
      this.setTitleHoverAt({
        openAt: { vx: vx - 50, vy: vy - BOX_WIDTH - 10 },
        title: idea.title,
      })
    }
    if (!idea || !idea.title) {
      this.hideTitleHover()
    }
  }

  hideTitleHover() {
    this.setTitleHoverAt(null)
  }

  setOpenedSquare({ gx: gx0, gy: gy0 }: Location): boolean {
    this.closeContextMenu()
    this.clearSelectedIdeas()

    const { gx, gy } = this.normalizeGridCoordsToIdea({ gx: gx0, gy: gy0 })

    if (this.openedCell && this.openedCell.gx === gx && this.openedCell.gy === gy) return false
    this.openedCell = { gx, gy }

    this.renderOpenedCellOverlay()

    return true
  }

  normalizeGridCoordsToIdea(location: Location) {
    const idea = this.model.getIdeaAt(location)
    if (idea) {
      return { gx: idea.location.gx, gy: idea.location.gy }
    } else {
      return location
    }
  }

  isIdeaOnscreen(idea: Idea): boolean {
    const { vx, vy } = plane_to_view(
      {
        px: idea.location.gx * BOX_WIDTH,
        py: idea.location.gy * BOX_WIDTH,
      },
      this.camera_state(),
    )
    return (
      vx > -BOX_WIDTH &&
      vx < this.renderer.screen_width() + BOX_WIDTH &&
      vy > -BOX_WIDTH &&
      vy < this.renderer.screen_height() + BOX_WIDTH
    )
  }

  isIdeaVisible(ideaId: ID): boolean {
    return this.isLocationVisible(this.model.fetchIdea(ideaId).location)
  }

  /**
   * Right edge of the free mosaic strip in canvas/view coordinates.
   * When the idea pane is open, that is the pane's left edge (not under the sheet).
   */
  visibleMosaicRightBound(): number {
    const canvas = this.domElement
    const pane = document.getElementById("visible-idea")
    if (canvas && pane) {
      const canvasRect = canvas.getBoundingClientRect()
      const paneRect = pane.getBoundingClientRect()
      const leftInCanvas = paneRect.left - canvasRect.left
      if (leftInCanvas > BOX_WIDTH) return leftInCanvas
    }
    return this.renderer.screen_width()
  }

  isLocationVisible(location: Location): boolean {
    const rightBound = this.visibleMosaicRightBound()
    const { vx, vy } = plane_to_view(
      {
        px: location.gx * BOX_WIDTH,
        py: location.gy * BOX_WIDTH,
      },
      this.camera_state(),
    )

    // Keep a cell-width margin so "visible" means comfortably in the free strip
    return (
      vx > BOX_WIDTH &&
      vy > BOX_WIDTH &&
      vx < rightBound - BOX_WIDTH &&
      vy < this.renderer.screen_height() - BOX_WIDTH
    )
  }

  scrollIntoView(location: Location): void {
    // Target: centre of free strip left of the idea pane, ~1/3 down the viewport
    const rightBound = this.visibleMosaicRightBound()
    this.viewBoundRightWhenOpened = rightBound
    const { px, py } = grid_to_plane(location)
    const midx = rightBound / 2
    const midy = this.renderer.screen_height() / 3
    const newsx = -1 * (px - midx / this.scale) * this.scale
    const newsy = -1 * (py - midy / this.scale) * this.scale
    this.movePlane({ vx: newsx, vy: newsy })
  }

  clearOpenedSquare(): void {
    this.openedCell = null
    this.renderer.hide_open_cell_overlay()
  }

  handleArrowKey(keyName: string) {
    if (!this.openedCell) return

    let newSquare: Location | null = null
    switch (keyName) {
      case "up":
        newSquare = this.upFromCell(this.openedCell)
        break
      case "down":
        newSquare = this.downFromCell(this.openedCell)
        break
      case "left":
        newSquare = this.leftFromCell(this.openedCell)
        break
      case "right":
        newSquare = this.rightFromCell(this.openedCell)
        break
    }
    if (newSquare) NavigationHistoryState.set_open_location(newSquare)
  }

  upFromCell(cell: Location): Location | null {
    if (cell == null) return null
    return { gx: cell.gx, gy: cell.gy - 1 }
  }

  downFromCell(cell: Location): Location | null {
    if (cell == null) return null
    const idea = this.model.getIdeaAt(cell)
    return {
      gx: cell.gx,
      gy: cell.gy + (idea ? ideaHeight(idea) : 1),
    }
  }

  leftFromCell(cell: Location): Location | null {
    if (cell == null) return null
    return { gx: cell.gx - 1, gy: cell.gy }
  }

  rightFromCell(cell: Location): Location | null {
    if (cell == null) return null
    const idea = this.model.getIdeaAt(cell)
    return {
      gx: cell.gx + (idea ? ideaWidth(idea) : 1),
      gy: cell.gy,
    }
  }

  handleFastArrowKey(keyName: string): void {
    if (this.openedCell == null) return
    const newSquare: Location | null = {
      gx: this.openedCell.gx,
      gy: this.openedCell.gy,
    }

    switch (keyName) {
      case "alt+up":
        newSquare.gy -= FAST_ARROW_JUMP_SIZE
        break
      case "alt+down":
        newSquare.gy += FAST_ARROW_JUMP_SIZE
        break
      case "alt+left":
        newSquare.gx -= FAST_ARROW_JUMP_SIZE
        break
      case "alt+right":
        newSquare.gx += FAST_ARROW_JUMP_SIZE
        break
      default:
        throw "unknown alt+arrow key"
    }

    NavigationHistoryState.set_open_location(newSquare)

    if (!this.isLocationVisible(newSquare)) this.scrollIntoView(newSquare)
  }

  addToSelectedIdeas(ideaId: ID): void {
    if (this.selectedIdeaIds.includes(ideaId)) return
    this.selectedIdeaIds.push(ideaId)
    this.clearOpenedSquare()
  }

  clearSelectedIdeas(): void {
    this.selectedIdeaIds = []
    this.renderIdeaSelection(true)
  }

  lassoArea({ gx1, gy1, gx2, gy2 }): void {
    const width = gx2 - gx1 + 1
    const height = gy2 - gy1 + 1
    this.lassoBox = { gx: gx1, gy: gy1, width, height }
    this.renderer.show_lasso(this.lassoBox)
  }

  clearLasso({ addToSelection }: { addToSelection: boolean } = { addToSelection: false }): void {
    this.renderer.hide_lasso()
    if (this.lassoBox != null) {
      if (!addToSelection) this.clearSelectedIdeas()
      const { gx: gx1, gy: gy1, width, height } = this.lassoBox
      for (let gx = gx1; gx < gx1 + width; gx++)
        for (let gy = gy1; gy < gy1 + height; gy++) {
          const idea = this.model.getIdeaAt({ gx, gy })
          if (idea != null) this.addToSelectedIdeas(idea.id)
        }
      this.renderIdeaSelection(true)
    }
    this.lassoBox = null
  }

  renderIdeaSelection(
    reRender: boolean,
    { dpx, dpy }: { dpx: number; dpy: number } = { dpx: 0, dpy: 0 },
  ): void {
    this.renderer.render_selection(this.selectedIdeas(), reRender, { dpx, dpy })
  }

  goHome() {
    this.renderer.reset_view({
      app_width: this.appWidth,
      app_height: this.appHeight,
    })
    this.scale = 1
    this.updateFullTitleMode({ immediate: true })
  }

  movePlane({ vx, vy }: { vx: number; vy: number }): void {
    const { vx: vx1, vy: vy1 } = this.clampViewCoordsToExtents({ vx, vy })
    this.renderer.move_view({ vx: vx1, vy: vy1 })
    this.renderer.reposition_grid(this.camera_state())

    this.closeContextMenu()
    this.updateClickHereFirstLocation()
    this.scheduleFullTitleRefresh()
  }

  clampViewCoordsToExtents({ vx, vy }: { vx: number; vy: number }): {
    vx: number
    vy: number
  } {
    if (!this.domElement) throw new Error("no dom element in clampViewCoordsToExtents")
    let { px: minpx, py: minpy } = grid_to_plane(this.extents.max)
    let { px: maxpx, py: maxpy } = grid_to_plane(this.extents.min)
    minpx = (minpx - BOX_WIDTH * 4) * this.scale
    maxpx = (maxpx + BOX_WIDTH * 4) * this.scale
    minpy = (minpy - BOX_WIDTH * 4) * this.scale
    maxpy = (maxpy + BOX_WIDTH * 4) * this.scale

    let vx1 = Math.min(Math.max(vx, -1 * minpx), -1 * (maxpx - this.domElement.clientWidth))
    let vy1 = Math.min(Math.max(vy, -1 * minpy), -1 * (maxpy - this.domElement.clientHeight))
    return { vx: vx1, vy: vy1 }
  }

  hideIdeas(ideaIds: ID[]): void {
    this.renderer.hide_idea_texts(ideaIds)
    this.renderer.hide_full_titles(ideaIds)
    this.renderFreshIdeas(ideaIds.map((id) => this.model.fetchIdea(id)))
  }

  showIdeas(ideaIds: ID[]): void {
    this.renderFreshIdeas()
    this.renderer.show_idea_texts(ideaIds)
    this.scheduleFullTitleRefresh()
  }

  moveIdeas(ideaLocations: [ID, Location][]): void {
    this.renderFreshIdeas()
    ideaLocations.map(([id, _location]) => {
      const idea = this.model.fetchIdea(id)
      this.renderer.position_idea_text(idea)
    })
    this.scheduleFullTitleRefresh()
  }

  ideaAdded(id: ID): void {
    let idea = this.model.fetchIdea(id)
    this.renderFreshIdeas()
    if (this.colourMode !== "default") this.renderer.rerender_rendered_idea_texts(this.model)
    this.renderer.render_idea_text(idea)
    this.scheduleFullTitleRefresh()
  }

  ideaUpdated(id: ID, dirtyField: string): void {
    let idea = this.model.fetchIdea(id)

    let isDirtyGraphic = false
    let isDirtyText = false

    if (dirtyField === "lastViewedAt" && this.colourMode === "lastViewedTime") {
      this.renderFreshIdeas()
      this.renderer.rerender_rendered_idea_texts(this.model)
      this.scheduleFullTitleRefresh({ immediate: true })
      return
    }

    if (["content", "memo", "title"].includes(dirtyField) && this.colourMode === "editTime") {
      this.renderFreshIdeas()
      this.renderer.rerender_rendered_idea_texts(this.model)
      this.scheduleFullTitleRefresh({ immediate: true })
      return
    }

    if (
      this.colourMode === "self" &&
      (dirtyField === "sourceType" || dirtyField === "sourceId")
    ) {
      this.renderFreshIdeas()
      this.renderer.rerender_rendered_idea_texts(this.model)
      this.scheduleFullTitleRefresh({ immediate: true })
      return
    }

    if (["type", "width", "height", "colour", "connections"].includes(dirtyField))
      isDirtyGraphic = true
    if (["colour", "memo", "title"].includes(dirtyField)) isDirtyText = true

    if (isDirtyText) this.renderer.rerender_idea_text(idea)
    if (this.renderer.has_idea_graphics() && isDirtyGraphic) {
      this.renderFreshIdeas()
    }
    if (isDirtyText || isDirtyGraphic || dirtyField === "location")
      this.scheduleFullTitleRefresh({ immediate: true })
  }

  removeIdeas(ideas: Idea[]): void {
    if (!ideas.length) return

    ideas.forEach((idea) => {
      const { gx, gy } = idea.location
      if (this.openedCell && this.openedCell.gx === gx && this.openedCell.gy === gy)
        this.clearOpenedSquare()
      this.selectedIdeaIds = this.selectedIdeas()
        .filter((idea) => idea.location.gx !== gx && idea.location.gy !== gy)
        .map((idea) => idea.id)
      this.renderIdeaSelection(true)

      if (this.dragIdeas) this.dragIdeas.removeIdeaAt({ gx, gy })
      this.renderer.clear_idea_text(idea)
      this.renderer.hide_full_titles([idea.id])
    })
    this.renderFreshIdeas()
    if (this.colourMode !== "default") this.renderer.rerender_rendered_idea_texts(this.model)
    this.scheduleFullTitleRefresh({ immediate: true })
  }

  openContextMenuAt(pl: PlaneLocation): void {
    const gl = plane_to_grid(pl)
    const idea = this.model.getIdeaAt(gl)
    if (idea) {
      const { vx, vy } = plane_to_view(pl, this.camera_state())
      this.setContextMenuState({
        on: "idea",
        at: { vx, vy },
        colour: idea.colour,
        targetedIdeaIds: this.targetedIdeaIds(idea),
      })
    } else {
      this.setContextMenuState({
        on: "blank",
        at: plane_to_view(pl, this.camera_state()),
      })
    }
  }

  closeContextMenu(): void {
    this.setContextMenuState(null)
  }

  targetedIdeaIds(idea: Idea): ID[] | null {
    let ids = this.selectedIdeaIds
    if (!ids.length) ids = [idea.id]
    return ids
  }

  destroy(): void {
    this.cancelScheduledFullTitleRefresh()
    this.removeDarkModeListener()
    this.renderer.destroy()
  }
}

export { PlateView, PlaneOffset, ViewBounds }
